// Utility for managing recently visited stocks in localStorage

export interface RecentStock {
  ticker: string;
  companyName?: string;
  visitedAt: string; // ISO date string
}

const STORAGE_KEY = 'recentlyVisitedStocks';
const MAX_RECENT_STOCKS = 10; // Store more than we display for filtering

export function getRecentStocks(): RecentStock[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored) as RecentStock[];
  } catch {
    return [];
  }
}

export function addRecentStock(ticker: string, companyName?: string): void {
  const upperTicker = ticker.toUpperCase();
  const currentStocks = getRecentStocks();
  
  // Remove existing entry for this ticker if present
  const filtered = currentStocks.filter(s => s.ticker !== upperTicker);
  
  // If no company name provided, check if we have one stored already
  const existingEntry = currentStocks.find(s => s.ticker === upperTicker);
  const resolvedCompanyName = companyName || existingEntry?.companyName;
  
  // Add new entry at the beginning
  const newEntry: RecentStock = {
    ticker: upperTicker,
    companyName: resolvedCompanyName,
    visitedAt: new Date().toISOString(),
  };
  
  const updated = [newEntry, ...filtered].slice(0, MAX_RECENT_STOCKS);
  
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // localStorage might be full or disabled
    console.warn('Failed to save recent stock to localStorage');
  }
}

export function formatTimeAgo(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  
  return date.toLocaleDateString();
}

